@if (isLoading) {
<div class="loading-state">
  <i class="fas fa-spinner fa-spin"></i>
  <span>Indlæser begivenhedsoplysninger...</span>
</div>
} @if (error) {
<div class="error-state">
  <i class="fas fa-exclamation-circle"></i>
  <div class="error-content">
    <span>{{ error }}</span>
    <!-- Show login component if login is required -->
    @if (showLoginComponent) {
    <div class="inline-login-container">
      <app-duna-login></app-duna-login>
    </div>
    }
    <!-- Fallback login button (for backwards compatibility) -->
    @if (showLoginButton && !showLoginComponent) {
    <button mat-raised-button color="primary" (click)="navigateToLogin()">
      <mat-icon>login</mat-icon>
      Log ind
    </button>
    }
  </div>
</div>
} @if (hasValidData) {
<div class="event-information-layout">
  <div class="event-information-main">
<div class="event-information">
  <!-- Sub-Event Warning Banner (per-day registration only) -->
  @if (isSubEvent && !sharesParticipantsAcrossSeries) {
  <div class="sub-event-warning-banner">
    <div class="warning-content">
      <i class="material-icons warning-icon">warning</i>
      <div class="warning-text">
        <span class="warning-title">Underbegivenhed</span>
        <span class="warning-message">{{ subEventWarning }}</span>
      </div>
      @if (masterEventGuid) {
      <button
        mat-raised-button
        color="accent"
        (click)="navigateToMasterEvent()"
      >
        <i class="material-icons">arrow_upward</i>
        Gå til hovedbegivenhed
      </button>
      }
    </div>
  </div>
  }
  <!-- Shared series: extra date — register via anchor / main event -->
  @if (isSharedSeriesExtraDay) {
  <div class="sub-event-warning-banner">
    <div class="warning-content">
      <i class="material-icons warning-icon">info</i>
      <div class="warning-text">
        <span class="warning-title">{{ seriesExtraDayWarningTitle }}</span>
        <span class="warning-message">{{ seriesExtraDayWarningMessage }}</span>
      </div>
      @if (isSharedLinkedGroupSeries ? linkedGroupRegistrationAnchorGuid :
      masterEventGuid) {
      <button
        mat-raised-button
        color="accent"
        (click)="
          isSharedLinkedGroupSeries
            ? navigateToLinkedGroupAnchor()
            : navigateToMasterEvent()
        "
      >
        <i class="material-icons">event</i>
        Tilmeld dig via {{ seriesRegistrationAnchorLabel }}
      </button>
      }
    </div>
  </div>
  }
  <!-- Current User Participation Status Banner -->
  @if (isCurrentUserParticipating) {
  <div
    class="participation-status-banner"
    [class]="getParticipantStatusClass()"
  >
    <div class="status-content">
      <i [class]="getParticipantStatusIcon()"></i>
      <span class="status-text">{{ getParticipantStatusText() }}</span>
    </div>
  </div>
  }
  <div class="event-header">
    <div class="event-header-content">
      <h2>{{ eventTitle }}</h2>
      <button
        mat-icon-button
        class="qr-button"
        (click)="showQRCode()"
        matTooltip="Vis QR kode for denne begivenhed"
        aria-label="Vis QR kode"
      >
        <mat-icon>qr_code</mat-icon>
      </button>
    </div>
    <div class="event-badges">
      <!-- Event Type Badge -->
      <span class="badge event-type">
        @switch (event!.event_type) { @case ('course') { Kursus } @case
        ('education') { Uddannelse } @case ('exercise') { Øvelse } @case
        ('activity') { Aktivitet } @default {
        {{ event!.event_type | titlecase }}
        } }
      </span>
      @if (shouldShowVisibilityLabel(event!.visibility)) {
      <!-- Visibility Badge -->
      <span class="badge visibility">
        @switch (event!.visibility) { @case ('public') { Offentlig } @case
        ('shared') { Delt } @case ('private') { Privat } @default {
        {{ event!.visibility | titlecase }}
        } }
      </span>
      }
      @if (!event!.is_active) {
      <span class="badge status">Inaktiv</span>
      }
    </div>
  </div>
  <!-- Action buttons container - only visible in modal mode or when user can edit -->
  @if (showFooter || userCanEditEvent) {
  <div class="action-buttons-container">
    <!-- Go to event button - only visible in modal mode -->
    @if (showFooter) {
    <button mat-raised-button color="primary" (click)="navigateToEvent()">
      <i [class]="footerButtonIcon"></i>
      {{ footerButtonText }}
    </button>
    }
    <!-- Edit button - only visible for users with specific roles -->
    @if (userCanEditEvent) {
    <button mat-raised-button color="primary" (click)="navigateToEditEvent()">
      <i class="fas fa-edit"></i>
      Rediger
    </button>
    }
  </div>
  }
  <div class="event-details">
    <!-- Date and Time -->
    @if (formattedDate) {
    <div class="detail-group">
      <div class="detail-row">
        <i class="material-icons">calendar_today</i>
        <span>{{ formattedDate }}</span>
      </div>
      <div class="detail-row">
        <i class="material-icons">access_time</i>
        <span>{{ formattedTime }}</span>
      </div>
    </div>
    }
    <!-- Location -->
    @if (location) {
    <div class="detail-group">
      <div class="detail-row">
        <i class="material-icons">location_on</i>
        <span>{{ location }}</span>
      </div>
      <!-- Enhanced location details when using full objects -->
      @if (event!.predefined_location) {
      <!-- Location Address -->
      @if (locationAddress) {
      <div class="detail-row">
        <i class="material-icons">home</i>
        <a
          [href]="getGoogleMapsUrl(locationAddress!)"
          (click)="openAddressInGoogleMaps(locationAddress!, $event)"
          target="_blank"
          rel="noopener noreferrer"
          class="address-link"
        >
          {{ locationAddress }}
        </a>
      </div>
      } @if (event!.predefined_room) {
      <div class="detail-row">
        <i class="material-icons">meeting_room</i>
        <span
          >{{ event!.predefined_room.room_name }}@if
          (event!.manual_location_room_capacity == null) { (Kapacitet:
          {{ event!.predefined_room.capacity }}) }</span
        >
      </div>
      } @for (extraRoom of extraRooms; track extraRoom.guid) {
      <div class="detail-row">
        <i class="material-icons">meeting_room</i>
        <span
          >Ekstra lokale: {{ extraRoom.room_name }}@if (extraRoom.capacity) {
          (Kapacitet: {{ extraRoom.capacity }}) }</span
        >
      </div>
      }
      <!-- Room Description -->
      @if (roomDescription) {
      <div class="detail-row room-description">
        <i class="material-icons">info</i>
        <span>{{ roomDescription }}</span>
      </div>
      } }
    </div>
    }
    <!-- Organization -->
    @if (event!.organization) {
    <div class="detail-group">
      <div class="detail-row">
        <i class="material-icons">business</i>
        <!-- If organization is not available, show the org_name as a fallback -->
        <span>
          {{ event!.organization.data?.name ?? event!.organization.org_name }}
        </span>
      </div>
    </div>
    }
    <!-- Instructors Section -->
    @if (instructors.length > 0) {
    <div class="detail-group instructors-section">
      <div class="detail-row instructors-header">
        <i class="material-icons">school</i>
        <span class="instructors-label">Instruktører:</span>
      </div>
      <div class="instructors-list">
        @for (instructor of instructors; track instructor.user.uuid) {
        <div class="instructor-item">
          <div class="instructor-info">
            <span class="instructor-name">
              {{ instructor.user.first_name }} {{ instructor.user.last_name }}
            </span>
          </div>
        </div>
        }
      </div>
    </div>
    }
    <!-- Capacity -->
    @if (eventCapacity !== null) {
    <div class="detail-group">
      <div class="detail-row">
        <i class="material-icons">people</i>
        <span>
          @if ((isLoggedIn$ | async)) {
          {{ participantCount }} / {{ eventCapacity }} deltagere @if
          ((canViewWaitlistInfo() | async)) { ({{ remainingSpotsLabel }}) } }
          @if (!(isLoggedIn$ | async)) { Kapacitet: {{ eventCapacity }} personer
          @if ((canViewWaitlistInfo() | async)) { ({{ remainingSpotsLabel }}) }
          }
        </span>
      </div>
      @if (event!.wait_list_type !== 'no_wait_list' && userCanEditEvent) {
      <div class="detail-row waitlist-count">
        <i class="material-icons">playlist_add</i>
        <span>{{ waitlistCountLabel }}</span>
      </div>
      }
      @if (userCanEditEvent) {
      <div class="detail-row cancellation-count">
        <i class="material-icons">event_busy</i>
        <span>{{ cancellationCountLabel }}</span>
      </div>
      }
    </div>
    }
    <!-- Waitlist - Only visible to orgbruger, orgadmin, superadmin -->
    @if (event!.wait_list_type !== 'no_wait_list' && (canViewWaitlistInfo() |
    async)) {
    <div class="detail-group">
      <div class="detail-row waitlist">
        <i class="material-icons">playlist_add_check</i>
        <span>
          Venteliste: @switch (event!.wait_list_type) { @case ('room_size') {
          <span>Baseret på lokalets kapacitet</span>
          } @case ('everyone_on_wait_list') {
          <span>Alle tilmeldinger går på venteliste</span>
          } }
        </span>
      </div>
    </div>
    }
    <!-- Catering -->
    @if (event!.catering) {
    <div class="detail-group">
      <div class="detail-row">
        <i class="material-icons">restaurant</i>
        <span>
          @if (event!.catering === 'catering_not_included') {
          <span>Forplejning ikke inkluderet</span>
          } @else {
          <span>Forplejning inkluderet</span>
          }
        </span>
      </div>
    </div>
    }
    <!-- Price -->
    @if (event!.price > 0) {
    <div class="detail-group">
      <div class="detail-row">
        <i class="material-icons">attach_money</i>
        <span
          >Pris:
          {{ event!.price | currency : "DKK" : "symbol" : "1.2-2" }}</span
        >
      </div>
    </div>
    }
  </div>
  @if (hasItems && !isModal) {
  <div class="event-items">
    <h3>Indhold</h3>
    <div class="items-grid">
      @for (item of eventItems; track item.id) {
      <div class="item-card" [class]="item.item_type">
        <!-- Enhanced item details display with polymorphic support -->
        @switch (item.item_type) {
        <!-- Subtype -->
        @case ('subtypes') { @if (item.item_details &&
        isSubTypeDTO(item.item_details)) {
        <div class="item-details">
          <div class="item-header" (click)="toggleModule(item.item_details)">
            <div class="item-name">{{ item.item_details.name }}</div>
            <button
              class="expand-button"
              mat-icon-button
              type="button"
              (click)="
                $event.stopPropagation(); toggleModule(item.item_details)
              "
            >
              <mat-icon>{{
                item.item_details.subtype_id &&
                expandedModules[item.item_details.subtype_id]
                  ? "keyboard_arrow_up"
                  : "keyboard_arrow_down"
              }}</mat-icon>
            </button>
          </div>
          @if (item.item_details.subtype_id &&
          expandedModules[item.item_details.subtype_id]) {
          <div class="item-description">
            {{ getModuleDescription(item.item_details) }}
          </div>
          }
        </div>
        } }
        <!-- Goal -->
        @case ('goals') { @if (item.item_details &&
        isGoalDTO(item.item_details)) {
        <div class="item-details">
          <div class="item-header" (click)="toggleModule(item.item_details)">
            <div class="item-name">{{ item.item_details.name }}</div>
            <button
              class="expand-button"
              mat-icon-button
              type="button"
              (click)="
                $event.stopPropagation(); toggleModule(item.item_details)
              "
            >
              <mat-icon>{{
                item.item_details.goal_id &&
                expandedModules[item.item_details.goal_id]
                  ? "keyboard_arrow_up"
                  : "keyboard_arrow_down"
              }}</mat-icon>
            </button>
          </div>
          @if (item.item_details.goal_id &&
          expandedModules[item.item_details.goal_id]) {
          <div class="item-description">
            {{ getModuleDescription(item.item_details) }}
          </div>
          }
        </div>
        } }
        <!-- Module -->
        @case ('modules') { @if (item.item_details &&
        isModuleDTO(item.item_details)) {
        <div class="item-details">
          <div class="item-header" (click)="toggleModule(item.item_details)">
            <div class="item-name">{{ item.item_details.name }}</div>
            <button
              class="expand-button"
              mat-icon-button
              type="button"
              (click)="
                $event.stopPropagation(); toggleModule(item.item_details)
              "
            >
              <mat-icon>{{
                item.item_details.module_id &&
                expandedModules[item.item_details.module_id]
                  ? "keyboard_arrow_up"
                  : "keyboard_arrow_down"
              }}</mat-icon>
            </button>
          </div>
          @if (item.item_details.module_id &&
          expandedModules[item.item_details.module_id]) {
          <div class="item-description">
            {{ getModuleDescription(item.item_details) }}
          </div>
          }
        </div>
        } }
        <!-- Educational Path -->
        @case ('educationalpaths') { @if (item.item_details &&
        isEducationalPathDTO(item.item_details)) {
        <div class="item-details">
          <div class="item-header" (click)="toggleModule(item.item_details)">
            <div class="item-name">{{ item.item_details.name }}</div>
            <button
              class="expand-button"
              mat-icon-button
              type="button"
              (click)="
                $event.stopPropagation(); toggleModule(item.item_details)
              "
            >
              <mat-icon>{{
                item.item_details.path_id &&
                expandedModules[item.item_details.path_id]
                  ? "keyboard_arrow_up"
                  : "keyboard_arrow_down"
              }}</mat-icon>
            </button>
          </div>
          @if (item.item_details.path_id &&
          expandedModules[item.item_details.path_id]) {
          <div class="item-description">
            {{ getModuleDescription(item.item_details) }}
          </div>
          }
        </div>
        } } }
      </div>
      }
    </div>
  </div>
  }
  <!-- Event Timeline -->
  @if (hasRelatedEvents) {
  <div class="event-timeline">
    <h3>{{ timelineTitle }}</h3>
    <div class="timeline-container">
      @if (!isLoadingRelatedEvents) {
      <div class="timeline-events">
        @for (relatedEvent of relatedEvents; track relatedEvent.guid; let i = $index)
        {
        <div
          class="timeline-event"
          [class.current-event]="isCurrentEvent(relatedEvent)"
          [class.main-event]="isMainEvent(relatedEvent)"
          [class.sub-event]="!isMainEvent(relatedEvent)"
          [class.participated-event]="hasUserParticipated(relatedEvent)"
          (click)="navigateToRelatedEvent(relatedEvent)"
        >
          <div class="event-date">
            <div class="date-text">{{ getEventDate(relatedEvent) }}</div>
            <div class="time-text">{{ getEventTime(relatedEvent) }}</div>
          </div>
          <div class="event-info">
            <div class="event-title">{{ getEventTitle(relatedEvent) }}</div>
            <div class="event-badges">
              @if (isMainEvent(relatedEvent)) {
              <span class="badge main-badge">
                {{ getRelatedEventBadgeLabel(relatedEvent, true) }}
              </span>
              } @if (!isMainEvent(relatedEvent)) {
              <span class="badge sub-badge">
                {{ getRelatedEventBadgeLabel(relatedEvent, false) }}
              </span>
              } @if (isCurrentEvent(relatedEvent)) {
              <span class="badge current-badge">Aktuel</span>
              } @if (hasUserParticipated(relatedEvent)) {
              <span class="badge participated-badge">Deltaget</span>
              }
            </div>
          </div>
          @if (!isCurrentEvent(relatedEvent)) {
          <div class="event-arrow">
            <i class="material-icons">arrow_forward</i>
          </div>
          }
        </div>
        }
      </div>
      } @if (isLoadingRelatedEvents) {
      <div class="timeline-loading">
        <i class="fas fa-spinner fa-spin"></i>
        <span>Indlæser begivenhedsserie...</span>
      </div>
      }
    </div>
  </div>
  } @if (longDescription) {
  <div class="event-description">
    <h3>Beskrivelse</h3>
    <p>{{ longDescription }}</p>
  </div>
  }
  @if ((isLoggedIn$ | async) && canViewEventFiles) {
  <div class="event-files">
    <div class="event-files-header">
      <h3>{{ eventFilesFolderTitle }}</h3>
      @if (isInsideEventFolder) {
      <button type="button" class="event-files-back" (click)="goBackEventFolder()">
        <mat-icon>arrow_back</mat-icon>
        Tilbage
      </button>
      }
    </div>
    @if (isLoadingEventFiles) {
    <div class="event-files-loading">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Indlæser filer...</span>
    </div>
    } @else if (!hasAnyEventFiles) {
    <p class="event-files-empty">Der er ingen filer tilknyttet denne begivenhed endnu.</p>
    } @else if (showEventFileCategories && !isInsideEventFolder) {
    <div class="event-files-groups">
      <section class="event-files-group participant">
        <div class="event-files-group-header">
          <mat-icon>people</mat-icon>
          <div>
            <h4>Til deltagere</h4>
            <p>Synlige for tilmeldte deltagere</p>
          </div>
        </div>
        @if (participantEventFolders.length === 0 && participantEventFiles.length === 0) {
        <p class="event-files-empty">Ingen deltagerfiler</p>
        }
        <div class="event-files-list">
          @for (folder of participantEventFolders; track folder.obfuscated_name) {
          <button type="button" class="event-file-row folder" (click)="openEventFolder(folder)">
            <mat-icon>{{ getEventFileIcon(folder) }}</mat-icon>
            <span class="event-file-name">{{ folder.display_name || folder.original_name }}</span>
            <span class="event-file-meta">Mappe</span>
          </button>
          } @for (file of participantEventFiles; track file.obfuscated_name) {
          <div class="event-file-row" [class.disabled]="!isEventFileReady(file)">
            <mat-icon>{{ getEventFileIcon(file) }}</mat-icon>
            <span class="event-file-name">{{ file.display_name || file.original_name }}</span>
            <span class="event-file-meta">{{ formatEventFileSize(file.file_size) }}</span>
            <div class="event-file-actions">
              <button
                mat-icon-button
                type="button"
                [disabled]="!isEventFileReady(file)"
                (click)="previewEventFile(file)"
                matTooltip="Forhåndsvis"
                aria-label="Forhåndsvis fil"
              >
                <mat-icon>visibility</mat-icon>
              </button>
              <button
                mat-icon-button
                type="button"
                [disabled]="!isEventFileReady(file)"
                (click)="downloadEventFile(file)"
                matTooltip="Download"
                aria-label="Download fil"
              >
                <mat-icon>download</mat-icon>
              </button>
            </div>
          </div>
          }
        </div>
      </section>
      <section class="event-files-group instructor">
        <div class="event-files-group-header">
          <mat-icon>school</mat-icon>
          <div>
            <h4>Til instruktører</h4>
            <p>Kun synlige for instruktører og redaktører</p>
          </div>
        </div>
        @if (instructorEventFolders.length === 0 && instructorEventFiles.length === 0) {
        <p class="event-files-empty">Ingen instruktørfiler</p>
        }
        <div class="event-files-list">
          @for (folder of instructorEventFolders; track folder.obfuscated_name) {
          <button type="button" class="event-file-row folder" (click)="openEventFolder(folder)">
            <mat-icon>{{ getEventFileIcon(folder) }}</mat-icon>
            <span class="event-file-name">{{ folder.display_name || folder.original_name }}</span>
            <span class="event-file-meta">Mappe</span>
          </button>
          } @for (file of instructorEventFiles; track file.obfuscated_name) {
          <div class="event-file-row" [class.disabled]="!isEventFileReady(file)">
            <mat-icon>{{ getEventFileIcon(file) }}</mat-icon>
            <span class="event-file-name">{{ file.display_name || file.original_name }}</span>
            <span class="event-file-meta">{{ formatEventFileSize(file.file_size) }}</span>
            <div class="event-file-actions">
              <button
                mat-icon-button
                type="button"
                [disabled]="!isEventFileReady(file)"
                (click)="previewEventFile(file)"
                matTooltip="Forhåndsvis"
                aria-label="Forhåndsvis fil"
              >
                <mat-icon>visibility</mat-icon>
              </button>
              <button
                mat-icon-button
                type="button"
                [disabled]="!isEventFileReady(file)"
                (click)="downloadEventFile(file)"
                matTooltip="Download"
                aria-label="Download fil"
              >
                <mat-icon>download</mat-icon>
              </button>
            </div>
          </div>
          }
        </div>
      </section>
    </div>
    } @else {
    <div class="event-files-list">
      @for (folder of visibleEventFolders; track folder.obfuscated_name) {
      <button type="button" class="event-file-row folder" (click)="openEventFolder(folder)">
        <mat-icon>{{ getEventFileIcon(folder) }}</mat-icon>
        <span class="event-file-name">{{ folder.display_name || folder.original_name }}</span>
        <span class="event-file-meta">Mappe</span>
      </button>
      } @for (file of visibleEventFiles; track file.obfuscated_name) {
      <div class="event-file-row" [class.disabled]="!isEventFileReady(file)">
        <mat-icon>{{ getEventFileIcon(file) }}</mat-icon>
        <span class="event-file-name">{{ file.display_name || file.original_name }}</span>
        <span class="event-file-meta">{{ formatEventFileSize(file.file_size) }}</span>
        <div class="event-file-actions">
          <button
            mat-icon-button
            type="button"
            [disabled]="!isEventFileReady(file)"
            (click)="previewEventFile(file)"
            matTooltip="Forhåndsvis"
            aria-label="Forhåndsvis fil"
          >
            <mat-icon>visibility</mat-icon>
          </button>
          <button
            mat-icon-button
            type="button"
            [disabled]="!isEventFileReady(file)"
            (click)="downloadEventFile(file)"
            matTooltip="Download"
            aria-label="Download fil"
          >
            <mat-icon>download</mat-icon>
          </button>
        </div>
      </div>
      }
    </div>
    }
  </div>
  }
  <!-- Other participants (same location or all org, depending on org setting) -->
  @if ((isLoggedIn$ | async) && participantsToShow.length > 0) {
  <div class="same-location-section">
    <div class="same-location-header">
      <i class="material-icons">people_outline</i>
      <span>{{ participantsSectionLabel }}</span>
    </div>
    <div class="same-location-chips">
      @for (p of participantsToShow; track p.user.uuid) {
      <span class="same-location-chip">
        {{ p.user.first_name }} {{ p.user.last_name }}
      </span>
      }
    </div>
  </div>
  } @if (showFooter) {
  <div class="event-footer">
    <button mat-raised-button color="primary" (click)="navigateToEvent()">
      <i [class]="footerButtonIcon"></i>
      {{ footerButtonText }}
    </button>
  </div>
  }
  <!-- Show message for sub-events when user hasn't participated in main event -->
  @if (isModal && isSubEvent && !sharesParticipantsAcrossSeries &&
  !hasUserParticipatedInMainEvent()) {
  <div class="event-footer-message">
    <div class="participation-required-message">
      <i class="material-icons">info</i>
      <span
        >Du skal først deltage i hovedbegivenheden for at få adgang til denne
        underbegivenhed.</span
      >
      <button
        mat-raised-button
        color="accent"
        (click)="navigateToMasterEvent()"
      >
        <i class="material-icons">arrow_upward</i>
        Gå til hovedbegivenhed
      </button>
    </div>
  </div>
  }
  <!-- Shared series: register via anchor / main event -->
  @if (isModal && isSharedSeriesExtraDay) {
  <div class="event-footer-message">
    <div class="participation-required-message">
      <i class="material-icons">info</i>
      <span
        >Tilmeld dig via {{ seriesRegistrationAnchorLabel }} for at deltage i
        hele serien.</span
      >
      <button
        mat-raised-button
        color="accent"
        (click)="
          isSharedLinkedGroupSeries
            ? navigateToLinkedGroupAnchor()
            : navigateToMasterEvent()
        "
      >
        <i class="material-icons">event</i>
        Gå til tilmelding
      </button>
    </div>
  </div>
  }
</div>
  </div>
  @if (showParticipateForm) {
  <aside class="event-information-participate" aria-label="Tilmelding">
    <app-duna-user-participate></app-duna-user-participate>
  </aside>
  }
</div>
}

<!-- QR Code Modal -->
@if (showQRModal) {
<div #qrModalOverlay class="qr-modal-overlay" (click)="closeQRCode()">
  <div class="qr-modal-content" (click)="$event.stopPropagation()">
    <div class="qr-modal-header">
      <h3>QR Kode for Begivenhed</h3>
      <button mat-icon-button (click)="closeQRCode()" class="close-button">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="qr-modal-body">
      <div class="qr-code-container">
        <qr-code
          [value]="eventQRData"
          [size]="256"
          [errorCorrectionLevel]="'M'"
        >
        </qr-code>
        <div class="qr-logo-overlay">
          <span class="duna-logo">DUNA</span>
        </div>
      </div>
      <div class="qr-info">
        <p>Scan denne QR kode for at få direkte adgang til denne begivenhed</p>
        <button
          type="button"
          class="qr-url"
          (click)="copyEventLink(); $event.stopPropagation()"
          matTooltip="Klik for at kopiere link"
        >
          <small>{{ eventQRData }}</small>
          <mat-icon class="copy-icon">{{
            linkCopied ? "check" : "content_copy"
          }}</mat-icon>
          @if (linkCopied) {
          <span class="copy-feedback">Kopieret!</span>
          }
        </button>
      </div>
    </div>
  </div>
</div>
}
